import { Model } from "@bryntum/schedulerpro";
import { HasProjectMixin } from "./mixins/HasProjectMixin.jsx";
import { formatLocationLines } from "@/react/components/entityViews/location/utils/locationDisplay";

export type ReadinessState = "thin" | "partial" | "field_ready";

export class Location extends HasProjectMixin(Model) {
  static get $name() {
    return "Location";
  }

  declare id: number | string;
  declare isPhantom: boolean;

  // ─── Address ────────────────────────────────────────────────────────────────

  /** @example "742 Evergreen Terrace" */
  declare streetAddress: string;

  /** @example "Springfield" */
  declare city: string;

  /** @example "IL" */
  declare stateOrRegion: string;

  /** @example "62701" */
  declare postalCode: string;

  /** @example "United States" */
  declare country: string;

  /** @example 37.7749 */
  declare latitude: number | null;

  /** @example -122.4194 */
  declare longitude: number | null;

  // ─── Site identity ──────────────────────────────────────────────────────────

  /** @example "Ashcroft Commons" */
  declare propertyName: string;

  /** @example "Building B" */
  declare buildingName: string;

  // ─── Work area ──────────────────────────────────────────────────────────────

  /** @example "3rd Floor" */
  declare floorOrLevel: string;

  /** @example "North Elevation" */
  declare elevation: string;

  /** @example "Parapet Return at NE Corner" */
  declare workZoneName: string;

  // ─── Access ─────────────────────────────────────────────────────────────────

  /** @example "Use rear service entrance off Maple Ave, ring buzzer #2" */
  declare primaryEntryRoute: string;

  /** @example "Rear lot near dumpster enclosure, 2 spaces reserved" */
  declare stagingArea: string;

  // ─── Guidance ───────────────────────────────────────────────────────────────

  /** @example "Check in with front desk, ask for John — he'll take you to the roof" */
  declare startHereNote: string;

  // ─── System ─────────────────────────────────────────────────────────────────

  /** Computed completeness state. @example "ready" */
  declare readinessState: ReadinessState;

  /** @example "3rd floor parapet, north elevation, Building B — Ashcroft Commons" */
  declare locationSummary: string;

  /** @example "Confirm roof hatch key with site super before dispatch" */
  declare workforceHandoffNote: string;

  declare createdAt: Date;
  declare updatedAt: Date;

  static get fields() {
    return [
      // Address
      { name: "streetAddress", dataSource: "street_address", type: "string" },
      { name: "city", type: "string" },
      { name: "stateOrRegion", dataSource: "state_or_region", type: "string" },
      { name: "postalCode", dataSource: "postal_code", type: "string" },
      { name: "country", type: "string" },
      { name: "latitude", type: "number" },
      { name: "longitude", type: "number" },

      // Site identity
      { name: "propertyName", dataSource: "property_name", type: "string" },
      { name: "buildingName", dataSource: "building_name", type: "string" },

      // Work area
      { name: "floorOrLevel", dataSource: "floor_or_level", type: "string" },
      { name: "elevation", type: "string" },
      { name: "workZoneName", dataSource: "work_zone_name", type: "string" },

      // Access
      { name: "primaryEntryRoute", dataSource: "primary_entry_route", type: "string" },
      { name: "stagingArea", dataSource: "staging_area", type: "string" },

      // Guidance
      { name: "startHereNote", dataSource: "start_here_note", type: "string" },

      // System
      { name: "readinessState", dataSource: "readiness_state", type: "string", defaultValue: "thin" },
      { name: "locationSummary", dataSource: "location_summary", type: "string" },
      { name: "workforceHandoffNote", dataSource: "workforce_handoff_note", type: "string" },
      { name: "createdAt", dataSource: "created_at", type: "date" },
      { name: "updatedAt", dataSource: "updated_at", type: "date" },
    ];
  }

  get aiGenerateSummaryPayload() {
    return {
      street_address: this.streetAddress || undefined,
      city: this.city || undefined,
      state_or_region: this.stateOrRegion || undefined,
      postal_code: this.postalCode || undefined,
      country: this.country || undefined,
      latitude: this.latitude ?? undefined,
      longitude: this.longitude ?? undefined,
      property_name: this.propertyName || undefined,
      building_name: this.buildingName || undefined,
      floor_or_level: this.floorOrLevel || undefined,
      elevation: this.elevation || undefined,
      work_zone_name: this.workZoneName || undefined,
      primary_entry_route: this.primaryEntryRoute || undefined,
      staging_area: this.stagingArea || undefined,
      start_here_note: this.startHereNote || undefined,
    };
  }


  get locationValue() {
    const { subLine, heading } = formatLocationLines(this);
    return subLine || heading || "";
  }
}

// @ts-expect-error - exposeProperties is internal method
Location.exposeProperties();
